import { useEffect, useRef, useState } from "react";

import { type DealOverviewSectionResponse } from "@vega-api/generated/types/dealOverviewSectionResponse";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import {
  INTERSECTION_OBSERVER,
  SECTION_IDS,
} from "../constants/overviewConstants";

type CustomSectionsProps = {
  sections?: DealOverviewSectionResponse[];
};

export const CustomSections = ({ sections }: CustomSectionsProps) => {
  const [visibleSections, setVisibleSections] = useState<Set<string>>(
    new Set(),
  );
  const observerRef = useRef<IntersectionObserver | null>(null);
  const elementsRef = useRef<Map<string, HTMLDivElement>>(new Map());

  useEffect(() => {
    if (!sections || sections.length === 0) return;

    const observerOptions = {
      root: null,
      rootMargin: INTERSECTION_OBSERVER.ROOT_MARGIN,
      threshold: INTERSECTION_OBSERVER.THRESHOLD,
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.getAttribute("data-section-id");

          if (sectionId) {
            setVisibleSections((prev) => new Set(prev).add(sectionId));
            observerRef.current?.unobserve(entry.target);
          }
        }
      });
    }, observerOptions);

    elementsRef.current.forEach((element) => {
      observerRef.current?.observe(element);
    });

    return () => observerRef.current?.disconnect();
  }, [sections]);

  if (!sections || sections.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      {sections.map((section) => (
        <div
          className={cnTwMerge(
            "space-y-4 transition-all duration-700 ease-out",
            visibleSections.has(section.id)
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-8",
          )}
          data-section-id={section.id}
          id={`${SECTION_IDS.SECTION_PREFIX}${section.id}`}
          key={section.id}
          ref={(el) => {
            if (el) {
              elementsRef.current.set(section.id, el);

              if (observerRef.current) {
                observerRef.current.observe(el);
              }
            }
          }}
        >
          <h3 className="text-text/primary body-large-semibold">
            {section.name}
          </h3>
          <div className="text-text/primary body-base-regular space-y-4 break-words">
            {section.content.split("\n").map((paragraph) => (
              <p key={`${section.id}-${paragraph.slice(0, 50)}`}>{paragraph}</p>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
